import React from 'react';
import { connect } from 'dva';
import { Alert, Input, Spin, Modal, Tooltip, Button } from 'antd';
import {
  VerticalAlignTopOutlined,
  ToTopOutlined,
  ShrinkOutlined,
  ArrowsAltOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import AppIcon from '@/components/app-icon';
import DraggableModal from '@/components/draggable-modal';
import { AI_ASSISTANT_SEED_MESSAGE } from '@/constants';
import ChatbotMessage from './chatbot-message';
import AiEula from './ai-eula';

import './index.less';

const INTRODUCTION =
  "Hi, I'm your CEC AI Assistant who has been trained to answer questions regarding Navigator, RLS, 6500, Waveserver and CEC portal help documentation. What can I help you with?";
class Chatbot extends React.Component {
  state = {
    chatbotInputValue: '',
  };

  chatbotInputRef = React.createRef();

  componentDidUpdate(prevProps) {
    const {
      chatbotModel: { chatbotVisible, chatbotConversationId, error, postMessagesLoading },
    } = this.props;

    if (prevProps.chatbotModel.chatbotVisible !== chatbotVisible) {
      chatbotVisible ? this.connect() : this.disconnect();
    }

    if (prevProps.chatbotModel.error !== error) {
      this.scrollDown();
    }

    if (this.shouldFocusInput && prevProps.chatbotModel.postMessagesLoading && !postMessagesLoading) {
      this.shouldFocusInput = false;
      this.chatbotInputRef.current?.focus();
    }
  }

  componentWillUnmount() {
    this.disconnect();
  }

  scrollDown = (className) =>
    requestAnimationFrame(() => {
      const dom = document.querySelector('.chatbot-messages-container .ant-spin-container');
      if (!dom) return;

      let scrollTop = dom.scrollTop + dom.scrollHeight;
      const divs = dom.querySelectorAll(className);

      if (divs.length > 0) {
        const lastDiv = divs[divs.length - 1];
        scrollTop = lastDiv.offsetTop - dom.offsetTop;
      }

      dom.scrollTop = scrollTop;
    });

  connect = () => {
    const {
      chatbotModel: { chatPlatformInfo, chatbotSourceId, chatbotConversationId, chatbotPubsubToken },
      dispatch,
    } = this.props;

    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) return;

    if (!chatbotConversationId) {
      dispatch({
        type: 'chatbotModel/postMessages',
        payload: { content: AI_ASSISTANT_SEED_MESSAGE },
        callback: this.connect,
      });
      return;
    }

    dispatch({ type: 'chatbotModel/getMessages' }).then(this.scrollDown);

    if (!chatbotSourceId) return;

    const url = `${chatPlatformInfo.serviceUrl}/cable`.replace(/^http/, 'ws');
    this.websocket = new WebSocket(url);

    this.websocket.onopen = () => {
      const message = JSON.stringify({
        command: 'subscribe',
        identifier: JSON.stringify({
          channel: 'RoomChannel',
          pubsub_token: chatbotPubsubToken,
        }),
      });
      this.websocket.send(message);
    };

    this.websocket.onmessage = (msg) => {
      const data = JSON.parse(msg.data);

      if (data.message && data.message.event === 'message.created') {
        dispatch({ type: 'chatbotModel/getMessages' }).then(() => this.scrollDown('.user-message'));
      }
    };

    this.websocket.onclose = () => {
      this.websocket = null;
    };

    this.websocket.onerror = (err) => {
      dispatch({ type: 'chatbotModel/setState', payload: { error: err.data } });
    };
    // If the server wasn't able to respond in 3 seconds then show some error message.
    setTimeout(() => {
      if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
        dispatch({
          type: 'chatbotModel/setState',
          payload: { error: 'WebSocket server unavailable' },
        });
      }
    }, 3000);
  };

  disconnect = () => {
    if (!this.websocket || this.websocket.readyState === WebSocket.CLOSED) {
      this.websocket = null;

      return;
    }

    this.websocket.close();
  };

  getRelatedMessage = (message) => {
    const {
      chatbotModel: { chatbotMessages },
    } = this.props;
    const { content_attributes = {} } = message;

    if (content_attributes.in_reply_to) {
      const replyToMessage = chatbotMessages.find((item) => item.id === content_attributes.in_reply_to);

      if (replyToMessage) return replyToMessage;
    }

    const index = chatbotMessages.findIndex((item) => item.id === message.id);
    if (index <= 0 || index >= chatbotMessages.length) return null;

    for (let i = index - 1; i >= 0; i--) {
      if (chatbotMessages[i].message_type !== chatbotMessages[index].message_type) {
        return chatbotMessages[i];
      }
    }

    return null;
  };

  render() {
    const {
      chatbotModel: {
        chatbotConversationId,
        chatbotVisible,
        chatbotDetached,
        chatbotExpanded,
        chatbotMessages,
        chatbotFeedback,
        loading,
        postMessagesLoading,
        error,
      },
      userModel: { profile },
      dispatch,
    } = this.props;
    const { chatbotInputValue, showAiEula } = this.state;
    const Container = chatbotDetached ? DraggableModal : 'div';

    if (!chatbotVisible) return null;

    if (!profile.aiEulaUpTodate)
      return (
        <AiEula
          onCancel={() =>
            dispatch({
              type: 'chatbotModel/setState',
              payload: {
                chatbotVisible: false,
              },
            })
          }
        />
      );

    return (
      <Container
        className={
          'chatbot-container ' +
          (chatbotDetached ? 'chatbot-detached ' : 'chatbot-attached ') +
          (chatbotExpanded ? 'chatbot-expanded ' : '')
        }
      >
        <div className="chatbot-toolbar">
          <div className="chatbot-toolbar-left">
            <AppIcon
              svg="app-bar"
              name="ai-assistant"
            />
            AI Assistant
          </div>
          <div className="chatbot-toolbar-right">
            <Tooltip
              placement="bottom"
              title="Acknowledgements"
            >
              <Button
                type="text"
                icon={<AppIcon name="setting" />}
                onClick={() => {
                  this.setState({ showAiEula: true });
                }}
              />
            </Tooltip>
            <Tooltip
              placement="bottom"
              color="white"
              title={
                <Button
                  type="text"
                  style={{ margin: '-6px -8px' }}
                  onClick={() => {
                    if (!chatbotConversationId) return;
                    Modal.confirm({
                      title: 'Confirm:',
                      content: 'Would you like to clear chat?',
                      cancelText: 'No',
                      okText: 'Yes',
                      cancelButtonProps: { type: 'text' },
                      onOk: () => {
                        this.disconnect();
                        dispatch({
                          type: 'chatbotModel/toggleConversationStatus',
                          payload: 'resolved',
                        }).then(this.connect);
                      },
                    });
                  }}
                >
                  Clear chat
                </Button>
              }
            >
              <Button
                type="text"
                icon={<AppIcon name="action-menu" />}
              />
            </Tooltip>
            {!chatbotDetached &&
              (chatbotExpanded ? (
                <Tooltip
                  placement="bottom"
                  title="Collapse"
                >
                  <Button
                    type="text"
                    icon={<VerticalAlignTopOutlined />}
                    onClick={() => {
                      dispatch({
                        type: 'chatbotModel/setState',
                        payload: {
                          chatbotExpanded: false,
                        },
                      });
                    }}
                  />
                </Tooltip>
              ) : (
                <Tooltip
                  placement="bottom"
                  title="Expand"
                >
                  <Button
                    type="text"
                    icon={<ToTopOutlined />}
                    onClick={() => {
                      dispatch({
                        type: 'chatbotModel/setState',
                        payload: {
                          chatbotExpanded: true,
                        },
                      });
                    }}
                  />
                </Tooltip>
              ))}
            {chatbotDetached ? (
              <Tooltip
                placement="bottom"
                title="Attach"
              >
                <Button
                  type="text"
                  icon={<ShrinkOutlined />}
                  onClick={() => {
                    dispatch({
                      type: 'chatbotModel/setState',
                      payload: {
                        chatbotDetached: false,
                      },
                    });
                  }}
                />
              </Tooltip>
            ) : (
              <Tooltip
                placement="bottom"
                title="Detach"
              >
                <Button
                  type="text"
                  icon={<ArrowsAltOutlined />}
                  onClick={() => {
                    dispatch({
                      type: 'chatbotModel/setState',
                      payload: {
                        chatbotDetached: true,
                      },
                    });
                  }}
                />
              </Tooltip>
            )}
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={() => {
                dispatch({
                  type: 'chatbotModel/setState',
                  payload: {
                    chatbotVisible: false,
                  },
                });
              }}
            />
          </div>
        </div>
        <div className="chatbot-body">
          <Spin
            wrapperClassName="chatbot-messages-container"
            spinning={loading || postMessagesLoading}
          >
            {chatbotMessages.length > 0 ? (
              chatbotMessages.map((message) => (
                <ChatbotMessage
                  key={message.id}
                  feedback={chatbotFeedback[message.id]}
                  onFeedback={(feedback) => {
                    let payload = {};
                    payload[message.id] = feedback;
                    dispatch({ type: 'chatbotModel/updateChatbotFeedback', payload });

                    /* Matomo */
                    const relatedMessage = this.getRelatedMessage(message);
                    if (!relatedMessage) return;

                    const data = { question: relatedMessage.content, feedback };
                    let customDimensionsObj = {};
                    customDimensionsObj[
                      `dimension${window._platformInfo.customDimensions.AIAssistantFeedback}`
                    ] = JSON.stringify(data);
                    dispatch({
                      type: 'userModel/trackUserEvent',
                      payload: { action: 'AI Assistant', data, customDimensionsObj },
                    });
                  }}
                  {...message}
                />
              ))
            ) : (
              <div className="introduction-message">{INTRODUCTION}</div>
            )}
            {error && (
              <Alert
                closable
                showIcon
                type="warning"
                message={`Bot encountered an error: ${error}.`}
              />
            )}
          </Spin>
          <div className="chatbot-footer">
            <Input.Search
              ref={this.chatbotInputRef}
              placeholder="Place your query here..."
              enterButton={<AppIcon name="send-message-icon" />}
              allowClear
              disabled={postMessagesLoading}
              value={chatbotInputValue}
              onChange={(e) => {
                this.setState({ chatbotInputValue: e.target.value });
              }}
              onSearch={(value) => {
                if (!value) return;
                this.shouldFocusInput = true;
                dispatch({
                  type: 'chatbotModel/postMessages',
                  payload: { content: value },
                  callback: () => {
                    this.connect();
                    this.setState({ chatbotInputValue: '' });
                  },
                });

                /* Matomo */
                const isNumberIntended = /\d/.test(value) && !/\p{L}/u.test(value);
                if (isNumberIntended) return;

                const data = { question: value };
                let customDimensionsObj = {};
                customDimensionsObj[
                  `dimension${window._platformInfo.customDimensions.AIAssistantFeedback}`
                ] = JSON.stringify(data);
                dispatch({
                  type: 'userModel/trackUserEvent',
                  payload: { action: 'AI Assistant', data, customDimensionsObj },
                });
              }}
            />
            <div className="chatbot-footer-info">
              Responses are generated by AI and may contain inaccuracies. Users are responsible for
              verifying before use.
            </div>
          </div>
        </div>
        {showAiEula && <AiEula onCancel={() => this.setState({ showAiEula: false })} />}
      </Container>
    );
  }
}

export default connect(({ chatbotModel, userModel }) => ({
  chatbotModel,
  userModel,
}))(Chatbot);
